import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ChartDashboardPayload, ChartDimensionKey } from '@cotera/api';
import {
  applyDashboardFilters,
  toggleDashboardFilter,
  type DashboardActiveFilters,
} from './apply-dashboard-filters';

type ChartDashboardContextValue = {
  payload: ChartDashboardPayload;
  filters: DashboardActiveFilters;
  toggleFilter: (dimension: ChartDimensionKey, value: string) => void;
  clearFilters: () => void;
  clearDimension: (dimension: ChartDimensionKey) => void;
};

const ChartDashboardContext = createContext<ChartDashboardContextValue | null>(
  null
);

export function useChartDashboard(): ChartDashboardContextValue {
  const ctx = useContext(ChartDashboardContext);
  if (ctx === null) {
    throw new Error(
      'useChartDashboard must be used within ChartDashboardProvider'
    );
  }
  return ctx;
}

export const ChartDashboardProvider: React.FC<{
  payload: ChartDashboardPayload;
  children: React.ReactNode;
}> = ({ payload, children }) => {
  const [filters, setFilters] = useState<DashboardActiveFilters>({});

  const toggleFilter = useCallback(
    (dimension: ChartDimensionKey, value: string) => {
      setFilters((prev) => toggleDashboardFilter(prev, dimension, value));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const clearDimension = useCallback((dimension: ChartDimensionKey) => {
    setFilters((prev) => ({ ...prev, [dimension]: null }));
  }, []);

  const value = useMemo(
    () => ({
      payload,
      filters,
      toggleFilter,
      clearFilters,
      clearDimension,
    }),
    [payload, filters, toggleFilter, clearFilters, clearDimension]
  );

  return (
    <ChartDashboardContext.Provider value={value}>
      {children}
    </ChartDashboardContext.Provider>
  );
};

export function useFilteredDashboardCharts(): ReturnType<
  typeof applyDashboardFilters
>[] {
  const { payload, filters } = useChartDashboard();
  return useMemo(
    () => payload.charts.map((chart) => applyDashboardFilters(chart, filters)),
    [payload.charts, filters]
  );
}
